/**
 * Promise-based confirm on the shared CodConfirm (app-components, redesign
 * P2.5) — App.vue hosts the one dialog instance via useConfirmHost().
 *
 * Contract (the old admin promise API, preserved):
 *   - resolve value is `true | false | null` when no formFields
 *     (true = buttonTrue, false = buttonFalse, null = cancel/backdrop/ESC)
 *   - resolve value is `{ confirm: boolean | null, data: Record<string, any> }`
 *     when options.formFields is present
 *   - buttons render only when their text is provided; the header only when
 *     title/icon are (the shared dialog's POS-style defaults are overridden)
 *
 * Dropped with the K-kit dissolution (redesign P2.5): the 500ms replace delay —
 * a confirm() while one is open displaces it immediately, and the displaced
 * promise resolves with the cancel shape instead of hanging (the old
 * singleton never resolved it); the colored toolbar (options.color) — the
 * design's modals carry one background (§4.5/AD-7); the select/checkbox/file
 * formFields branches — only `text` was ever exercised (Orders.vue einvoice
 * flows). The shared input superset (ICodConfirmInput) is the formFields
 * vocabulary now.
 */
import { nextTick, ref } from 'vue'
import type { CodConfirmColor, ICodConfirmConfig, ICodConfirmInput } from 'app-components'

export interface ConfirmOptions {
    /** a button renders only when its text is provided */
    buttonTrueText?: string | null
    buttonFalseText?: string | null
    buttonCancelText?: string | null
    buttonTrueColor?: CodConfirmColor
    buttonFalseColor?: CodConfirmColor
    buttonCancelColor?: CodConfirmColor
    /** header icon (decorative; header renders only when title/icon set) */
    icon?: string
    /** when true, backdrop/ESC do not dismiss (a button is the only exit) */
    persistent?: boolean
    title?: string | null
    /** dialog width in px (the shared dialog defaults to 420) */
    width?: number
    formFields?: ICodConfirmInput[]
}

export type ConfirmResult =
    | boolean
    | null
    | { confirm: boolean | null, data: Record<string, any> }

// Module-level singleton host state — App.vue renders the one CodConfirm
// bound to these refs.
const confirmOpen = ref(false)
const confirmConfig = ref<ICodConfirmConfig>({})

export function useConfirmHost() {
    return { confirmOpen, confirmConfig }
}

/**
 * Low-level raw-config entry to the singleton host. The kit's shared forms
 * consume it through the `codConfirm` injection (App.vue provides it);
 * useConfirm()'s promise API rides the same displacement rules.
 */
export function showConfirm(config: ICodConfirmConfig): void {
    if (confirmOpen.value) {
        // Displace through a real falling edge: the open dialog settles via
        // its cancelHandler, and the new config snapshots on a fresh rising edge.
        confirmOpen.value = false
        nextTick(() => {
            confirmConfig.value = config
            confirmOpen.value = true
        })
        return
    }

    confirmConfig.value = config
    confirmOpen.value = true
}

export function useConfirm() {
    function confirm(message: string, options: ConfirmOptions = {}): Promise<ConfirmResult> {
        return new Promise<ConfirmResult>(resolve => {
            const withFields = !!options.formFields?.length

            const config: ICodConfirmConfig = {
                message,
                // Explicit '' overrides the shared dialog's POS-style defaults:
                // old admin semantics are hidden-unless-provided.
                title: options.title ?? '',
                icon: options.icon ?? '',
                buttonTrueText: options.buttonTrueText ?? '',
                buttonFalseText: options.buttonFalseText ?? '',
                buttonCancelText: options.buttonCancelText ?? '',
                canDismiss: !options.persistent,
                yesHandler: values => resolve(withFields ? { confirm: true, data: values } : true),
                noHandler: values => resolve(withFields ? { confirm: false, data: values } : false),
                cancelHandler: values => resolve(withFields ? { confirm: null, data: values } : null)
            }

            if (options.buttonTrueColor) config.buttonTrueColor = options.buttonTrueColor
            if (options.buttonFalseColor) config.buttonFalseColor = options.buttonFalseColor
            if (options.buttonCancelColor) config.buttonCancelColor = options.buttonCancelColor
            if (options.width) config.width = `${options.width}px`
            if (withFields) config.inputs = options.formFields

            showConfirm(config)
        })
    }

    return { confirm }
}
